// src/pages/Airbnbs.js
import React, { useEffect, useState } from "react";
import { DataGrid } from "@mui/x-data-grid";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Box,
  FormControlLabel,
  Checkbox,
  Tabs,
  Tab,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
} from "@mui/material";
import axiosInstance from "../utils/axiosInstance";
import AirbnbImages from "../components/admin/AirbnbImages";
import AirbnbUnits from "../components/admin/AirbnbUnits";
import AirbnbAmenities from "../components/admin/AirbnbAmenities";
import AirbnbBadges from "../components/admin/AirbnbBadges";

// ✅ Helper function to get user's location
const getUserLocation = () => {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject("Geolocation is not supported by your browser.");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        resolve({
          latitude: pos.coords.latitude.toFixed(6),
          longitude: pos.coords.longitude.toFixed(6),
        });
      },
      (err) => reject(err.message || "Failed to get your location.")
    );
  });
};

const Airbnbs = () => {
  const [airbnbs, setAirbnbs] = useState([]);
  const [open, setOpen] = useState(false);
  const [editSlug, setEditSlug] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [propertyTypes, setPropertyTypes] = useState([]);
  const [form, setForm] = useState({
    name: "",
    description: "",
    location: "",
    base_price: "",
    contact: "",
    verified: false,
    active: true,
    latitude: "",
    longitude: "",
    check_in: "14:00",
    check_out: "11:00",
    rules: "",
    property_type: "bnb",
  });

  // Fetch all Airbnbs
  const fetchAirbnbs = async () => {
    try {
      const res = await axiosInstance.get("/airbnbs");
      setAirbnbs(res.data);
    } catch (error) {
      console.error(error);
    }
  };

  // Fetch property types
  const fetchPropertyTypes = async () => {
    try {
      const res = await axiosInstance.get("/airbnbs/property-types");
      setPropertyTypes(res.data);
    } catch (error) {
      console.error("Failed to fetch property types:", error);
    }
  };

  useEffect(() => {
    fetchAirbnbs();
    fetchPropertyTypes();
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm({ ...form, [name]: type === "checkbox" ? checked : value });
  };

  const handleOpen = async (airbnb = null) => {
    if (airbnb) {
      setEditSlug(airbnb.slug);
      try {
        const res = await axiosInstance.get(`/airbnbs/slug/${airbnb.slug}`);
        const data = res.data;
        setForm({
          name: data.name || "",
          description: data.description || "",
          location: data.location || "",
          base_price: data.base_price || "",
          contact: data.contact || "",
          verified: !!data.verified,
          active: data.active ?? true,
          latitude: data.latitude || "",
          longitude: data.longitude || "",
          check_in: data.check_in || "14:00",
          check_out: data.check_out || "11:00",
          rules: data.rules || "",
          property_type: data.property_type || "bnb",
        });
      } catch (error) {
        console.error("Failed to fetch Airbnb details:", error);
      }
    } else {
      setEditSlug(null);
      setForm({
        name: "",
        description: "",
        location: "",
        base_price: "",
        contact: "",
        verified: false,
        active: true,
        latitude: "",
        longitude: "",
        check_in: "14:00",
        check_out: "11:00",
        rules: "",
        property_type: "bnb",
      });
    }
    setActiveTab(0);
    setOpen(true);
  };

  const handleSubmit = async () => {
    try {
      if (editSlug) {
        const bnb = airbnbs.find((b) => b.slug === editSlug);
        if (!bnb) return;
        await axiosInstance.put(`/airbnbs/${bnb.id}`, form);
      } else {
        await axiosInstance.post("/airbnbs", form);
      }
      setOpen(false);
      fetchAirbnbs();
    } catch (error) {
      console.error(error);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this Airbnb?")) {
      try {
        await axiosInstance.delete(`/airbnbs/${id}`);
        fetchAirbnbs();
      } catch (error) {
        console.error(error);
      }
    }
  };

  const columns = [
    { field: "id", headerName: "ID", width: 60 },
    { field: "name", headerName: "Name", flex: 1 },
    { field: "location", headerName: "Location", flex: 1 },
    { field: "property_type", headerName: "Property Type", width: 150 },
    { field: "base_price", headerName: "Base Price", width: 120 },
    { field: "contact", headerName: "Contact", width: 150 },
    {
      field: "verified",
      headerName: "Verified",
      width: 100,
      renderCell: (params) => (params.value ? "✅" : "❌"),
    },
    {
      field: "active",
      headerName: "Active",
      width: 100,
      renderCell: (params) => (
        <Checkbox
          checked={params.value}
          onChange={async () => {
            const newStatus = !params.value;
            try {
              await axiosInstance.put(`/airbnbs/${params.row.id}`, {
                ...params.row,
                active: newStatus,
              });
              fetchAirbnbs();
            } catch (error) {
              console.error(error);
            }
          }}
        />
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 180,
      renderCell: (params) => (
        <>
          <Button onClick={() => handleOpen(params.row)} size="small">
            Edit
          </Button>
          <Button
            onClick={() => handleDelete(params.row.id)}
            color="error"
            size="small"
          >
            Delete
          </Button>
        </>
      ),
    },
  ];

  return (
    <Box sx={{ p: 2 }}>
      <h2>Stays Management</h2>
      <Button
        variant="contained"
        color="primary"
        onClick={() => handleOpen()}
        sx={{ mb: 2 }}
      >
        Add Airbnb
      </Button>

      <div style={{ height: 500, width: "100%", overflowX: "auto" }}>
        <DataGrid
          rows={airbnbs}
          columns={columns}
          pageSize={5}
          rowsPerPageOptions={[5]}
          autoHeight
        />
      </div>

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>{editSlug ? "Edit Airbnb" : "Add Airbnb"}</DialogTitle>
        <DialogContent>
          <Tabs value={activeTab} onChange={(e, val) => setActiveTab(val)}>
            <Tab label="Details" />
            {editSlug && <Tab label="Images" />}
            {editSlug && <Tab label="Units" />}
            {editSlug && <Tab label="Amenities" />}
            {editSlug && <Tab label="Badges" />}
          </Tabs>

          {/* DETAILS TAB */}
          {activeTab === 0 && (
            <Box sx={{ mt: 2 }}>
              <TextField label="Name" name="name" value={form.name} onChange={handleChange} fullWidth margin="normal" />
              <TextField label="Description" name="description" value={form.description} onChange={handleChange} fullWidth margin="normal" multiline rows={3} />
              <TextField label="Location" name="location" value={form.location} onChange={handleChange} fullWidth margin="normal" />
              <TextField label="Base Price" name="base_price" value={form.base_price} onChange={handleChange} fullWidth margin="normal" />
              <TextField label="Contact" name="contact" value={form.contact} onChange={handleChange} fullWidth margin="normal" />

              <FormControl fullWidth margin="normal">
                <InputLabel>Property Type</InputLabel>
                <Select
                  name="property_type"
                  value={form.property_type}
                  onChange={handleChange}
                >
                  {propertyTypes.map((type) => (
                    <MenuItem key={type} value={type}>
                      {type.charAt(0).toUpperCase() + type.slice(1).replace("_", " ")}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField label="Latitude" name="latitude" value={form.latitude} onChange={handleChange} fullWidth margin="normal" />
              <TextField label="Longitude" name="longitude" value={form.longitude} onChange={handleChange} fullWidth margin="normal" />

              {/* ✅ Use My Location Button */}
              <Button
                variant="outlined"
                onClick={async () => {
                  try {
                    const coords = await getUserLocation();
                    setForm((prev) => ({
                      ...prev,
                      latitude: coords.latitude,
                      longitude: coords.longitude,
                    }));
                    alert(`Location set: ${coords.latitude}, ${coords.longitude}`);
                  } catch (err) {
                    alert(err);
                  }
                }}
                sx={{ mt: 1, mb: 2 }}
              >
                Use My Location
              </Button>

              <TextField label="Check-in" name="check_in" value={form.check_in} onChange={handleChange} fullWidth margin="normal" />
              <TextField label="Check-out" name="check_out" value={form.check_out} onChange={handleChange} fullWidth margin="normal" />
              <TextField label="Rules" name="rules" value={form.rules} onChange={handleChange} fullWidth margin="normal" multiline rows={2} />
              <FormControlLabel control={<Checkbox checked={form.verified} onChange={handleChange} name="verified" />} label="Verified" />
              <FormControlLabel control={<Checkbox checked={form.active} onChange={handleChange} name="active" />} label="Active" />
            </Box>
          )}

          {editSlug && activeTab === 1 && <AirbnbImages airbnbId={airbnbs.find(b => b.slug===editSlug)?.id} />}
          {editSlug && activeTab === 2 && <AirbnbUnits airbnbId={airbnbs.find(b => b.slug===editSlug)?.id} />}
          {editSlug && activeTab === 3 && <AirbnbAmenities airbnbId={airbnbs.find(b => b.slug===editSlug)?.id} />}
          {editSlug && activeTab === 4 && <AirbnbBadges airbnbId={airbnbs.find(b => b.slug===editSlug)?.id} />}
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          {activeTab === 0 && (
            <Button onClick={handleSubmit} variant="contained" color="primary">
              Save
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Airbnbs;
